'use client';

import { createClient } from '@/lib/supabase/client';

export type InventoryItem = {
  id: string;
  productId: string;
  quantity: number;
  reservedQuantity: number;
  availableQuantity: number;
  lowStockThreshold: number;
  stockLabel: string;
  stockLabelZh: string;
  stockBadgeColor: string;
  updatedAt: string;
};

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '23') return false;
    if (errorClass === '08') return true;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return schemaErrorPatterns.some((p) => p.test(error.message));
  }
  return false;
}

function getStockInfo(available: number, threshold: number): { label: string; labelZh: string; badgeColor: string } {
  if (available <= 0) {
    return { label: 'Out of Stock', labelZh: '缺貨', badgeColor: 'bg-red-500' };
  }
  if (available <= threshold) {
    return { label: `Only ${available} left`, labelZh: `僅剩${available}件`, badgeColor: 'bg-yellow-500' };
  }
  return { label: 'In Stock', labelZh: '庫存充足', badgeColor: 'bg-green-500' };
}

function mapRow(row: any): InventoryItem {
  const available = Math.max(0, (row.quantity || 0) - (row.reserved_quantity || 0));
  const stockInfo = getStockInfo(available, row.low_stock_threshold || 10);
  return {
    id: row.id,
    productId: row.product_id,
    quantity: row.quantity,
    reservedQuantity: row.reserved_quantity,
    availableQuantity: available,
    lowStockThreshold: row.low_stock_threshold,
    stockLabel: stockInfo.label,
    stockLabelZh: stockInfo.labelZh,
    stockBadgeColor: stockInfo.badgeColor,
    updatedAt: row.updated_at,
  };
}

export const inventoryService = {
  async getAll(): Promise<InventoryItem[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('inventory')
        .select('*')
        .order('product_id', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error fetching inventory:', error.message);
          throw error;
        }
        console.log('Data error fetching inventory:', error.message);
        return [];
      }
      return data?.map(mapRow) || [];
    } catch (error: any) {
      console.log('Inventory fetch error:', error.message);
      throw error;
    }
  },

  async getByProductId(productId: string): Promise<InventoryItem | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('inventory')
        .select('*')
        .eq('product_id', productId)
        .maybeSingle();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error fetching inventory item:', error.message);
          throw error;
        }
        console.log('Data error fetching inventory item:', error.message);
        return null;
      }
      return data ? mapRow(data) : null;
    } catch (error: any) {
      console.log('Inventory item fetch error:', error.message);
      throw error;
    }
  },

  subscribeToChanges(callback: (items: InventoryItem[]) => void) {
    const supabase = createClient();
    const channel = supabase
      .channel('inventory_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'inventory' },
        async () => {
          const items = await inventoryService.getAll();
          callback(items);
        }
      )
      .subscribe();
    return () => supabase.removeChannel(channel);
  },
};
